import { Type } from '@angular/core';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { CalculatedFieldComponent } from '../../pages/form-builder/fields/calculated/view';
import { CheckboxGroupFieldComponent } from '../../pages/form-builder/fields/checkbox-group/view/checkbox-group-field.component';
import { CheckboxFieldComponent } from '../../pages/form-builder/fields/checkbox/view/checkbox-field.component';
import { DateTimeFieldComponent } from '../../pages/form-builder/fields/datetime/view/datetime-field.component';
import { DropdownFieldComponent } from '../../pages/form-builder/fields/dropdown/view/dropdown-field.component';
import { DurationFieldComponent } from '../../pages/form-builder/fields/duration/view/duration-field.component';
import { EmailFieldComponent } from '../../pages/form-builder/fields/email/view/email-field.component';
import { NumberFieldComponent } from '../../pages/form-builder/fields/number/view/number-field.component';
import { PhotoFieldComponent } from '../../pages/form-builder/fields/photo/view/photo-field.component';
import { RadioFieldComponent } from '../../pages/form-builder/fields/radio/view/radio-field.component';
import { SelectFieldComponent } from '../../pages/form-builder/fields/select/view/select-field.component';
import { TelFieldComponent } from '../../pages/form-builder/fields/tel/view/tel-field.component';
import { TextLabelFieldComponent } from '../../pages/form-builder/fields/text-label/view/text-label-field.component';
import { InputFieldComponent } from '../../pages/form-builder/fields/text/view/input-field.component';
import { TextareaFieldComponent } from '../../pages/form-builder/fields/textarea/view/textarea-field.component';
import {
  getCalculatedFieldConfigFields,
  getCheckBoxFieldConfigurationFields,
  getCheckboxGroupFieldConfigFields,
  getDateTimeFieldConfigFields,
  getDropdownFieldConfigFields,
  getDurationFieldConfigFields,
  getEmailFieldConfigFields,
  getNumberFieldConfigFields,
  getPhoneNumberFieldConfigFields,
  getPhotoFieldConfigFields,
  getRadioFieldConfigFields,
  getSelectFieldConfigFields,
  getTextareaFieldConfigFields,
  getTextFieldConfigFields,
  getTextLabelFieldConfigFields,
} from './field-config-forms';

/**
 * TEMPORARY BACKWARD COMPATIBILITY ALIASES
 *
 * Maps deprecated field type names to their current standardized names.
 * This is a temporary compatibility layer for old data that uses deprecated naming.
 *
 * DO NOT add new aliases. Instead, run a proper data migration to update all
 * records in the database. See `docs/database/schema-management-architecture.md`
 * for migration patterns.
 *
 * Known aliases:
 * - 'input' → 'text' (deprecated field type renamed in standardization)
 *   - Old test data may still use this name
 *   - Should be migrated via a proper migration script
 *   - Timeline: Plan migration post-Phase 2
 */
const FIELD_TYPE_ALIASES: Record<string, string> = {
  input: 'text', // TEMPORARY: Old field type name, standardized to 'text'
};

/**
 * Centralized field type configuration
 *
 * This is the single source of truth for all field type definitions.
 * Each field type includes:
 * - name: the unique field type identifier
 * - label: i18n key for the field type label
 * - icon: icon name for UI display
 * - viewComponent: the component used to display the field (for Formly)
 * - editFormDefinition: Formly-driven config editor fields
 *
 * This configuration is used by:
 * - field-type-selector.component.ts: to display available field types
 * - form-builder.component.ts: to get icons and editor components
 * - app.config.ts: to register Formly field types
 *
 * NEVER duplicate field type definitions elsewhere in the codebase.
 * Always use the helper functions provided below to extract specific views of the data.
 */
export interface FieldTypeConfig {
  name: string;
  label: string;
  icon: string;
  viewComponent: Type<any>;
  editFormDefinition: FormlyFieldConfig[];
}

export const FIELD_TYPES: FieldTypeConfig[] = [
  {
    name: 'calculated',
    label: 'formBuilder.fieldTypes.calculated',
    icon: 'calculator',
    viewComponent: CalculatedFieldComponent,
    editFormDefinition: getCalculatedFieldConfigFields(),
  },
  {
    name: 'text',
    label: 'formBuilder.fieldTypes.textInput',
    icon: 'type',
    viewComponent: InputFieldComponent,
    editFormDefinition: getTextFieldConfigFields(),
  },
  {
    name: 'datetime',
    label: 'formBuilder.fieldTypes.datetimeInput',
    icon: 'clock',
    viewComponent: DateTimeFieldComponent,
    editFormDefinition: getDateTimeFieldConfigFields(),
  },
  {
    name: 'duration',
    label: 'formBuilder.fieldTypes.duration',
    icon: 'timer',
    viewComponent: DurationFieldComponent,
    editFormDefinition: getDurationFieldConfigFields(),
  },
  {
    name: 'textarea',
    label: 'formBuilder.fieldTypes.textArea',
    icon: 'align-left',
    viewComponent: TextareaFieldComponent,
    editFormDefinition: getTextareaFieldConfigFields(),
  },
  {
    name: 'number',
    label: 'formBuilder.fieldTypes.number',
    icon: 'hash',
    viewComponent: NumberFieldComponent,
    editFormDefinition: getNumberFieldConfigFields(),
  },
  {
    name: 'email',
    label: 'formBuilder.fieldTypes.email',
    icon: 'mail',
    viewComponent: EmailFieldComponent,
    editFormDefinition: getEmailFieldConfigFields(),
  },
  {
    name: 'tel',
    label: 'formBuilder.fieldTypes.tel',
    icon: 'phone',
    viewComponent: TelFieldComponent,
    editFormDefinition: getPhoneNumberFieldConfigFields(),
  },
  {
    name: 'checkbox',
    label: 'formBuilder.fieldTypes.checkbox',
    icon: 'square-check',
    viewComponent: CheckboxFieldComponent,
    editFormDefinition: getCheckBoxFieldConfigurationFields(),
  },
  {
    name: 'radio',
    label: 'formBuilder.fieldTypes.radioButtons',
    icon: 'circle-dot',
    viewComponent: RadioFieldComponent,
    editFormDefinition: getRadioFieldConfigFields(),
  },
  {
    name: 'checkbox-group',
    label: 'formBuilder.fieldTypes.checkboxList',
    icon: 'list',
    viewComponent: CheckboxGroupFieldComponent,
    editFormDefinition: getCheckboxGroupFieldConfigFields(),
  },
  {
    name: 'select',
    label: 'formBuilder.fieldTypes.select',
    icon: 'layout-list',
    viewComponent: SelectFieldComponent,
    editFormDefinition: getSelectFieldConfigFields(),
  },
  {
    name: 'dropdown',
    label: 'formBuilder.fieldTypes.dropdown',
    icon: 'chevrons-up-down',
    viewComponent: DropdownFieldComponent,
    editFormDefinition: getDropdownFieldConfigFields(),
  },
  {
    name: 'photo',
    label: 'formBuilder.fieldTypes.photoCapture',
    icon: 'camera',
    viewComponent: PhotoFieldComponent,
    editFormDefinition: getPhotoFieldConfigFields(),
  },
  {
    name: 'text-label',
    label: 'formBuilder.fieldTypes.textLabel',
    icon: 'bookmark',
    viewComponent: TextLabelFieldComponent,
    editFormDefinition: getTextLabelFieldConfigFields(),
  },
];

/**
 * TEMPORARY: Resolve field type aliases to their canonical names.
 *
 * This function transparently converts deprecated field type names to their
 * current standardized names. It's used whenever field types are loaded from
 * data to ensure consistent behavior.
 *
 * Example:
 *   resolveFieldTypeAlias('input') → 'text'
 *   resolveFieldTypeAlias('text')  → 'text'
 *
 * @param fieldType The field type name (possibly an alias)
 * @returns The canonical field type name
 *
 * IMPORTANT: This is a TEMPORARY compatibility layer. Do not rely on this
 * for new code. Plan proper data migrations instead. See comment at top of file.
 */
export function resolveFieldTypeAlias(fieldType: string): string {
  return FIELD_TYPE_ALIASES[fieldType] || fieldType;
}

/**
 * Get available field types for the field type selector.
 *
 * NOTE: Does NOT include aliases - users should never see deprecated names
 * in the selector. Aliases are resolved transparently when loading data.
 *
 * @returns Array of {value, label} objects for field type selector
 */
export function getAvailableFieldTypes(): Array<{ value: string; label: string }> {
  return FIELD_TYPES.map((ft) => ({
    value: ft.name,
    label: ft.label,
  }));
}

/**
 * Get icon mapping for all field types, including aliases.
 * Used to display icons in the UI.
 *
 * Includes mappings for both canonical names and aliases to ensure that
 * old data using deprecated field type names can still be rendered correctly.
 *
 * @returns Record mapping field type names (including aliases) to icon names
 */
export function getFieldTypeIcons(): Record<string, string> {
  const icons: Record<string, string> = {};
  FIELD_TYPES.forEach((ft) => {
    icons[ft.name] = ft.icon;
  });

  // Add mappings for aliases so that old data can still be looked up
  // Example: 'input' → 'type' (the icon for 'text' field type)
  Object.entries(FIELD_TYPE_ALIASES).forEach(([alias, canonical]) => {
    const canonicalConfig = FIELD_TYPES.find((ft) => ft.name === canonical);
    if (canonicalConfig) {
      icons[alias] = canonicalConfig.icon;
    }
  });

  return icons;
}

/**
 * Get Formly type definitions for all field types, including aliases.
 * Used to register field types with Formly in the application configuration.
 *
 * Includes both canonical field types and aliases to ensure that old data
 * using deprecated field type names can still be rendered in forms.
 *
 * @returns Array of {name, component} objects for Formly registration
 */
export function getFormlyTypes(): Array<{ name: string; component: Type<any> }> {
  const types = FIELD_TYPES.map((ft) => ({
    name: ft.name,
    component: ft.viewComponent,
  }));

  // Add aliases for Formly registration
  // This allows old field type names to be resolved to their canonical components
  // Example: 'input' → InputFieldComponent (the view component for 'text' field type)
  Object.entries(FIELD_TYPE_ALIASES).forEach(([alias, canonical]) => {
    const canonicalType = types.find((t) => t.name === canonical);
    if (canonicalType) {
      types.push({
        name: alias,
        component: canonicalType.component,
      });
    }
  });

  return types;
}
